import { useFormContext } from "react-hook-form";
import YesNoOptions from "../../../../../constants/options/YesNoOptions";
import { classes } from "../../../../../util/style-utils";
import RadioButtonGroup from "../../../../gds/RadioButtonGroup";
import InputBox from "../../../../gds/InputBox";

/**
 * Employee Information Section component that renders form fields for employee details
 * 
 * @component
 * @param {Object} props - The component props
 * @param {boolean} [props.showLicensedProducerQuestions=false] - Whether to display licensed producer related questions
 * @returns {JSX.Element} The rendered employee information form section
 * 
 * @description This component displays:
 * - Years with Agency input field
 * - Licensed producer question (conditionally shown)
 * - National Producer Number (NPN) input field (conditionally shown)
 * 
 * The component uses react-hook-form for form management and validation.
 * Form fields are conditionally rendered based on the showLicensedProducerQuestions prop
 * and the user's response to the licensed producer question.
 * 
 * @example
 * // Basic usage without producer questions
 * <EmployeeInfoSection />
 * 
 * @example
 * // With producer questions enabled
 * <EmployeeInfoSection showLicensedProducerQuestions={true} />
 */
export default function EmployeeInfoSection({ showLicensedProducerQuestions = false }) {
    const { control, formState: { errors }, register, watch } = useFormContext();

    const watchIsLicensedProducer = watch("isLicensedProducer");

    return <>
        <h3 className="margin-top margin-bottom">Employee Info</h3>

        <div className="flex-row">
            <div className={classes("form-field", errors.timeAtAgency && "form-field--error")}>
                <label htmlFor="time-at-agency" className="text">
                    Years with Agency{/*
                    */}<span className='required'>*</span>
                </label>
                <input id="time-at-agency" type="text" {...register("timeAtAgency")} />
                {errors.timeAtAgency &&
                    <span className="form-message">{errors.timeAtAgency.message}</span>
                }
            </div>
        </div>

        {showLicensedProducerQuestions &&
            <>
                <RadioButtonGroup trackingId="shared:PersonalInformation:EmployeeInfoSection:LicensedProducer"
                    control={control}
                    name="isLicensedProducer"
                    legend={<>
                        Are you a licensed producer?{/*
                        */}<span className='required'>*</span>
                    </>}
                    layout="Two Column"
                    options={YesNoOptions}
                />

                <div className="flex-row">
                    <div className={classes("form-field", errors.producerNPN && "form-field--error")}>
                        <InputBox
                            control={control}
                            name="producerNPN"
                            label={
                                <>
                                    Individual National Producer Number (NPN)
                                    {watchIsLicensedProducer === "true" && <span className='required'>*</span>}
                                </>
                            }
                            description={watchIsLicensedProducer !== "true" ? "Optional" : undefined}
                            mask="000000000"
                            maskOptions={{
                                lazy: true
                            }}
                            fullWidth={true}
                            placeholder="NPN"
                        />
                    </div>
                </div>
            </>
        }
    </>;
}
